import { IUser } from '@forwardflow/types/user'
import LocalStorage from '@forwardflow/services/LocalStorage'

import {
   createContext,
   JSX,
   useContext,
   useEffect,
   useMemo,
   useState,
} from 'react'
import useAuthUser from '@forwardflow/hooks/customHooks/useAuthUser'

export type AuthProps = {
   user: IUser | null
   authenticated: boolean
   loading: boolean
   currentOrganization: string | null
}

export interface IAuthContext<T> {
   auth: T
   setAuth: React.Dispatch<React.SetStateAction<T>>
}

type AuthProviderProps = {
   children: React.ReactNode
}

const AuthContext = createContext<IAuthContext<AuthProps>>({
   auth: {
      user: null,
      authenticated: false,
      loading: false,
      currentOrganization: null,
   },
   setAuth: () => {},
})

export default function AuthProvider({
   children,
}: AuthProviderProps): JSX.Element {
   console.info('🚀 ~ AuthProvider ~ AuthProvider:')

   const initialAuth = {
      user: LocalStorage.get('user') as IUser | null,
      authenticated: LocalStorage.get('authenticated') === 'true',
      loading: true,
      currentOrganization: LocalStorage.get('currentOrganization') || null,
   }

   const [auth, setAuth] = useState<AuthProps>(initialAuth)

   const shouldFetch = initialAuth.authenticated

   const { data: user, isLoading } = useAuthUser({
      revalidateOnMount: shouldFetch,
   })

   useEffect(() => {
      setAuth((current) => ({ ...current, loading: isLoading }))
   }, [isLoading])

   useEffect(() => {
      if (user) {
         setAuth((current) => ({
            ...current,
            user: user as IUser,
            authenticated: true,
            currentOrganization:
               (user as IUser & { currentOrganization?: string })
                  .currentOrganization || '',
         }))

         LocalStorage.put('user', user)
         LocalStorage.put('authenticated', true)
         // LocalStorage.put(
         //    'currentOrganization',
         //    (user as IUser & { currentOrganization?: string })
         //       .currentOrganization || ''
         // )
      }
   }, [user])

   const value = useMemo(() => ({ auth, setAuth }), [auth])

   return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
const useAuth = () => {
   const context = useContext(AuthContext)
   if (!context) {
      throw new Error('useAuth must be used within an AuthProvider')
   }
   return context
}

export { useAuth, AuthContext }
